<template>
  <section class="dataset-export" aria-label="Export dataset">
    <header><h4><i class="bi bi-download" aria-hidden="true"></i> Export dataset</h4><span>All matching rows · not just this page</span></header>
    <p>Uses the currently selected columns and filters. Page size and cursor do not limit the export.</p>
    <div class="export-actions">
      <label>File format <select v-model="format" :disabled="!!attempt"><option value="csv">CSV</option><option value="tsv">TSV</option><option value="parquet">Parquet</option></select></label>
      <button type="button" :disabled="!!attempt || !!selectionError || !ready" @click="queue">Queue export</button>
    </div>
    <p v-if="format !== 'parquet'" class="warning">CSV/TSV preserves original values, including spreadsheet formulas. Open untrusted exports with spreadsheet formula execution disabled.</p>
    <p v-if="selectionError && !attempt" class="error">{{ selectionError }}</p>
    <template v-if="attempt">
      <p class="scope">Export snapshot: {{ attempt.columns.length }} columns · {{ attempt.filters.length }} filters · {{ attempt.format.toUpperCase() }}. Later query edits do not change this export.</p>
      <details><summary>Export columns and filters</summary><pre>{{ JSON.stringify(attempt, null, 2) }}</pre></details>
    </template>
    <p v-if="submitting" role="status">Submitting export…</p>
    <p v-if="cancelling" role="status">Cancelling export… The worker stops at its next checkpoint; the ready dataset remains unchanged.</p>
    <p v-if="job" role="status">Export #{{ job.id }}: <strong>{{ job.status }}</strong><span v-if="job.progress_rows != null"> · {{ job.progress_rows }} rows processed</span></p>
    <pre v-if="error || job?.error" class="error" role="alert">{{ error || job.error }}</pre>
    <div class="export-actions">
      <button v-if="retrySubmission" type="button" :disabled="submitting || cancelling" @click="queue">Retry identical export request</button>
      <button v-if="job" type="button" :disabled="polling || cancelling" @click="resume">{{ polling ? 'Checking…' : 'Refresh export status' }}</button>
      <button v-if="canCancel" type="button" :disabled="cancelling || submitting || !ready" @click="cancelExport">Cancel export</button>
      <a v-if="job?.status === 'succeeded' && !cancelling" class="download" :href="downloadUrl" target="_blank" rel="noopener noreferrer" referrerpolicy="no-referrer">Download {{ attempt.format.toUpperCase() }} <i class="bi bi-box-arrow-up-right" aria-hidden="true"></i></a>
      <button v-if="terminal" type="button" :disabled="cancelling" @click="resetAttempt">New export</button>
      <button v-if="attempt && !job && !submitting" type="button" :disabled="cancelling" @click="abandon">Abandon attempt</button>
    </div>
    <p v-if="job?.status === 'cancelled'" class="hint">This export was cancelled. No export download is available; the ready dataset and its rows remain unchanged. Choose New export to start another export.</p>
    <p v-if="job?.status === 'succeeded'" class="hint">Download is streamed by your browser in a separate tab; no full-file JavaScript buffer is created. If it reports 410 (expired), choose New export. For 409, refresh status and retry; 403/404 indicate unavailable access or artifact.</p>
    <p v-if="job && !terminal" class="hint">The worker continues if you close this panel. Closing loses this panel's job tracking; note job #{{ job.id }}.</p>
  </section>
</template>

<script>
import apiService from '@/services/apiService';
import { snapshotRowsQuery, rowsErrorMessage } from '@/utils/tabularRows';
import { cryptographicKey, ambiguousImportError, positiveInteger } from '@/utils/tabularImport';
import { confirmDelete } from '@/utils/confirmDelete';

export default {
  name: 'TabularDatasetExport',
  props: {
    dataset: { type: Object, required: true }, entraId: { type: String, required: true },
    columns: { type: Array, required: true }, projection: { type: Array, required: true }, filters: { type: Array, required: true },
  },
  data() { return { format: 'csv', attempt: null, job: null, submitting: false, polling: false, cancelling: false, error: '', retrySubmission: false }; },
  computed: {
    ready() { return this.dataset.status === 'ready' && !!this.entraId?.trim(); },
    contextKey() { return JSON.stringify([this.dataset.id, this.entraId]); },
    selectionError() {
      try { snapshotRowsQuery(this.columns, this.projection, this.filters, 100); return ''; }
      catch (error) { return error.message; }
    },
    terminal() { return ['succeeded', 'failed', 'cancelled'].includes(this.job?.status); },
    canCancel() { return this.job?.type === 'export' && ['queued', 'running'].includes(this.job.status); },
    downloadUrl() { return this.job?.status === 'succeeded' ? apiService.datasetExportDownloadUrl(this.job.id, this.entraId) : ''; },
  },
  watch: {
    contextKey: { flush: 'sync', handler() { this.resetAttempt(); } },
    ready: { flush: 'sync', handler(value) { if (!value) this.stop(); } },
  },
  created() { this.generation = 0; this.disposed = false; },
  beforeUnmount() { this.disposed = true; this.stop(); },
  methods: {
    stop() {
      ++this.generation; clearTimeout(this.timer); this.controller?.abort(); this.cancelController?.abort();
      this.polling = false; this.cancelling = false;
    },
    resetAttempt() {
      this.stop(); this.attempt = null; this.job = null; this.error = ''; this.retrySubmission = false; this.submitting = false;
    },
    abandon() {
      if (this.cancelling) return;
      if (window.confirm('The previous request may already have queued an export. Abandoning its retry key and starting again can create a duplicate. Continue?')) this.resetAttempt();
    },
    async queue() {
      if (this.cancelling || this.submitting || !this.ready || (this.attempt && !this.retrySubmission)) return;
      this.error = '';
      try {
        if (!this.attempt) {
          const query = snapshotRowsQuery(this.columns, this.projection, this.filters, 100);
          this.attempt = Object.freeze({ columns: query.columns, filters: query.filters, format: this.format, idempotency_key: cryptographicKey() });
        }
      } catch (error) { this.error = error.message; return; }
      this.stop();
      const generation = this.generation;
      const controller = new AbortController(); this.controller = controller;
      const current = () => !this.disposed && generation === this.generation && !controller.signal.aborted;
      this.submitting = true; this.retrySubmission = false;
      try {
        const response = await apiService.createDatasetExport(this.dataset.id, this.attempt, this.entraId, controller.signal);
        if (!current()) return;
        this.checkJob(response?.job);
        this.job = response.job;
        if (!this.terminal) this.timer = setTimeout(() => this.poll(generation, 2000), 2000);
      } catch (error) {
        if (!current()) return;
        this.error = rowsErrorMessage(error);
        this.retrySubmission = ambiguousImportError(error) || error.response?.status === 429;
      } finally { if (current()) this.submitting = false; }
    },
    checkJob(job) {
      if (!positiveInteger(job?.id) || job.dataset_id !== this.dataset.id || job.type !== 'export' ||
          !['queued', 'running', 'succeeded', 'failed', 'cancelled'].includes(job.status) ||
          (this.job && String(this.job.id) !== String(job.id))) throw new Error('Export response does not contain the expected export job.');
    },
    async cancelExport() {
      if (this.disposed || this.cancelling || this.submitting || !this.ready || !this.canCancel) return;
      this.stop();
      const generation = this.generation;
      const context = this.contextKey;
      const jobId = this.job.id;
      const entraId = this.entraId;
      const controller = new AbortController(); this.cancelController = controller;
      const current = () => !this.disposed && generation === this.generation && context === this.contextKey &&
        !controller.signal.aborted && this.ready && this.job?.id === jobId;
      this.cancelling = true;
      let again = false;
      try {
        const confirmed = await confirmDelete('Cancel this export? The worker stops at its next checkpoint. The ready dataset and its rows remain unchanged.');
        if (!current()) return;
        if (!confirmed) { again = this.canCancel; return; }
        this.checkJob(this.job);
        if (!this.canCancel) return;
        this.error = '';
        const response = await apiService.cancelTabularJob(jobId, entraId, controller.signal);
        if (!current()) return;
        this.checkJob(response?.job);
        this.job = response.job;
        again = this.canCancel;
      } catch (error) {
        if (!current()) return;
        const status = error.response?.status;
        this.error = `Unable to cancel export${status ? ` (HTTP ${status})` : ''}. ${rowsErrorMessage(error)} Refresh export status to check the current outcome before trying again.`;
      } finally {
        if (current()) {
          this.cancelling = false;
          this.cancelController = null;
          if (again) this.timer = setTimeout(() => this.poll(generation, 2000), 2000);
        }
      }
    },
    resume() { if (this.cancelling || !this.job || !this.ready) return; this.stop(); this.poll(this.generation, 2000); },
    async poll(generation, delay) {
      if (this.disposed || this.cancelling || generation !== this.generation || !this.job || !this.ready) return;
      const controller = new AbortController(); this.controller = controller;
      const current = () => !this.disposed && generation === this.generation && !controller.signal.aborted;
      this.polling = true;
      let again = false;
      try {
        const response = await apiService.getTabularJob(this.job.id, this.entraId, controller.signal);
        if (!current()) return;
        this.checkJob(response?.job); this.job = response.job; this.error = '';
        again = !this.terminal; delay = 2000;
      } catch (error) {
        if (!current()) return;
        this.error = rowsErrorMessage(error);
        const status = error.response?.status;
        again = !status || status >= 500 || status === 429;
        delay = Math.min(delay * 2, 15000);
      } finally { if (current()) this.polling = false; }
      if (current() && again) this.timer = setTimeout(() => this.poll(generation, delay), delay);
    },
  },
};
</script>

<style scoped>
.dataset-export { border: 1px solid #d5e4d9; border-radius: 10px; padding: 18px; background: #f7faf8; }
header { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin-bottom: 10px; }
h4 { margin: 0; font-size: 1rem; color: #18392b; }
header span, .hint { font-size: .8rem; color: #607466; }
p { font-size: .85rem; margin: 10px 0; }
.export-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
label { display: flex; gap: 8px; align-items: center; }
button, select, .download { padding: 8px 12px; border: 1px solid #c5d6ca; border-radius: 6px; background: white; color: #18392b; }
.download { background: #18392b; color: white; text-decoration: none; }
button:disabled { opacity: .5; cursor: not-allowed; }
.warning { background: #fff3d4; color: #775711; border-radius: 5px; padding: 10px; }
.scope { font-weight: 500; }
.error { color: #9c2020; }
pre { white-space: pre-wrap; overflow-wrap: anywhere; max-height: 220px; overflow: auto; }
</style>